import React from "react";
import type { SettingsListRuntime } from "../../../../../shared/frontend/settings/settingsListTypes";
import { useAuth } from "../app/auth/useAuth";
import { useI18n } from "../app/i18n/useI18n";
import { useUiPreferences } from "../app/prefs/useUiPreferences";
import { StudioScopeWidenHint } from "../app/studios/StudioScopeWidenHint";
import { useGlobalStudioContext } from "../app/studios/useGlobalStudioContext";
import { useListValueFormatter } from "../app/utils/useListValueFormatter";

/**
 * Grundlaufzeit der Shared-Listen in m1member: Anmeldung, Texte, Serverpräferenzen, Wertformatierung und
 * die Kopfform des Produkts („page“: Titel über der Karte, wie m2company).
 */
export function useBaseSettingsListRuntime(): SettingsListRuntime {
  const auth = useAuth();
  const { text } = useI18n();
  const preferences = useUiPreferences();
  const formatValue = useListValueFormatter();
  const accountId = auth.info?.accountId ?? null;
  return React.useMemo<SettingsListRuntime>(() => ({
    accountId,
    text,
    sortPreferences: preferences,
    formatValue,
  }), [accountId, text, preferences, formatValue]);
}

/**
 * Laufzeit der Shared-Listen in m1member mit Studiokontext.
 * `withScope`: Hinweis zum Erweitern der Studio-Auswahl bei leerer oder kurzer Trefferliste.
 */
export function useSettingsListRuntime({ withScope = false }: { withScope?: boolean } = {}): SettingsListRuntime {
  const base = useBaseSettingsListRuntime();
  const scope = useGlobalStudioContext();
  return React.useMemo<SettingsListRuntime>(() => ({
    ...base,
    scope: withScope
      ? { key: scope.studioIdsQueryParam, loaded: scope.studiosLoaded, allowedCount: scope.allowedStudios.length, isAll: scope.isAllStudios, renderHint: (hitCount: number) => <StudioScopeWidenHint hitCount={hitCount} /> }
      : undefined,
  }), [base, withScope, scope.studiosLoaded, scope.allowedStudios.length, scope.isAllStudios, scope.studioIdsQueryParam]);
}
